// V17.299: AR Commercial daily team board API client.
//
// Types are imported from arTeamBoard.ts rather than redeclared — one payload
// shape, one set of types, so the two boards cannot drift apart silently.
//
// Endpoints:
//   GET    /api/v1/ar-commercial/team-board                     → human auth
//   GET    /api/v1/ar-commercial/team-board/export              → .xlsx blob
//   GET    /api/v1/ar-commercial/team-board/display             → X-Display-Token
//   POST   /api/v1/ar-commercial/team-board/display-tokens      → pair a TV
//   GET    /api/v1/ar-commercial/team-board/display-tokens      → list
//   DELETE /api/v1/ar-commercial/team-board/display-tokens/:id  → revoke

import { apiClient } from './api';
import type { DisplayToken, DisplayTokenIssued, TeamBoard } from './arTeamBoard';

const BASE = '/ar-commercial/team-board';

export async function fetchCommercialTeamBoard(): Promise<TeamBoard> {
  return apiClient.get<TeamBoard>(BASE);
}

/** Role-gated, so it must ride the authenticated apiClient — a plain link
 *  or window.open would 401. */
export async function exportCommercialTeamBoardXlsx(): Promise<Blob> {
  const response = await apiClient.get<{ data: Blob }>(
    `${BASE}/export`,
    undefined,
    { responseType: 'blob' },
  );
  return response.data;
}

/** Paired-display read. The token never rides in the query string after pairing. */
export async function fetchCommercialTeamBoardForDisplay(
  token: string,
): Promise<TeamBoard> {
  return apiClient.get<TeamBoard>(`${BASE}/display`, undefined, {
    headers: { 'X-Display-Token': token },
  });
}

export async function createCommercialDisplayToken(
  label: string,
): Promise<DisplayTokenIssued> {
  return apiClient.post<DisplayTokenIssued>(`${BASE}/display-tokens`, { label });
}

export async function listCommercialDisplayTokens(): Promise<DisplayToken[]> {
  return apiClient.get<DisplayToken[]>(`${BASE}/display-tokens`);
}

export async function revokeCommercialDisplayToken(id: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/display-tokens/${id}`);
}
